/* global process */
/**
 * Application configuration
 * Centralizes all configuration and environment variables
 * 
 * IMPORTANT: Les URLs sont configurées via les fichiers .env
 * - .env.development pour le développement local
 * - .env.production pour la production
 */

const getEnv = (viteKey, reactKey, fallback = undefined) => {
  const viteValue = typeof import.meta !== 'undefined' ? import.meta.env?.[viteKey] : undefined;
  const reactValue = typeof process !== 'undefined' ? process.env?.[reactKey] : undefined;
  return viteValue || reactValue || fallback;
};

const getBrowserOrigin = () => {
  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    return window.location.origin;
  }
  return undefined;
};

const getDefaultBaseUrl = () => {
  const browserOrigin = getBrowserOrigin();
  if (browserOrigin) {
    if (browserOrigin.includes('localhost') || browserOrigin.includes('127.0.0.1')) {
      return 'http://localhost:20002';
    }
    if (browserOrigin.includes('dashboard.nixia.app')) {
      return 'https://backend.nixia.app';
    }

    // Multi-domain deployments (CRM):
    // If the frontend is served on a crm-*.nixia.app hostname and the backend is on backend.<frontend-host>,
    // infer the backend origin to avoid calling the frontend origin (/api/v2 -> 403/404).
    try {
      const parsed = new URL(browserOrigin);
      const host = (parsed.hostname || '').toLowerCase();
      const isCrmFrontend = host.startsWith('crm-') && host.endsWith('.nixia.app');
      if (isCrmFrontend && !host.startsWith('backend.')) {
        const inferredBackend = `https://backend.${host}`;
        return inferredBackend;
      }
    } catch (e) {
      // Ignore URL parsing issues and keep the current fallback behavior.
    }

    return browserOrigin;
  }
  return 'https://backend.nixia.app';
};

const isLocalhostUrl = value => {
  if (!value) return false;
  return /localhost|127\.0\.0\.1/i.test(value);
};

const normalizeHttpsForKnownHosts = (value) => {
  if (!value) return value;
  if (typeof window === 'undefined' || !window.location) return value;
  if (window.location.protocol !== 'https:') return value;
  try {
    const url = new URL(value);
    if (url.protocol !== 'http:') return value;
    const host = (url.hostname || '').toLowerCase();
    if (host.endsWith('nixia.app') || host.endsWith('harnixsas.com')) {
      url.protocol = 'https:';
      return url.toString();
    }
    return value;
  } catch {
    return value;
  }
};

const runtimeEnv = getEnv('MODE', 'NODE_ENV', 'development');
const isProductionEnv = runtimeEnv === 'production';
const defaultBaseUrl = getDefaultBaseUrl();
const rawBaseUrl = getEnv('VITE_API_BASE_URL', 'REACT_APP_API_BASE_URL');
const rawApiUrl = getEnv('VITE_API_URL', 'REACT_APP_API_URL');

let resolvedBaseUrl = rawBaseUrl || defaultBaseUrl;
if (isProductionEnv && rawBaseUrl && isLocalhostUrl(rawBaseUrl)) {
  console.warn('API base URL points to localhost in production. Using inferred URL instead:', defaultBaseUrl);
  resolvedBaseUrl = defaultBaseUrl;
}
resolvedBaseUrl = normalizeHttpsForKnownHosts(resolvedBaseUrl);

let resolvedApiUrl = rawApiUrl || `${resolvedBaseUrl}/api/v2`;
if (isProductionEnv && rawApiUrl && isLocalhostUrl(rawApiUrl)) {
  console.warn('API URL points to localhost in production. Using inferred URL instead:', `${resolvedBaseUrl}/api/v2`);
  resolvedApiUrl = `${resolvedBaseUrl}/api/v2`;
}
resolvedApiUrl = normalizeHttpsForKnownHosts(resolvedApiUrl);

const config = {
  // API Configuration
  api: {
    // ✅ FIX: Supporte VITE_* et REACT_APP_* pour compatibilité
    baseUrl: resolvedBaseUrl || (() => {
      const inferred = defaultBaseUrl;
      console.warn('API base URL is not defined! Using inferred value:', inferred);
      return inferred; // Fallback minimal pour éviter les crashes
    })(),
    
    apiUrl: resolvedApiUrl || (() => {
      const baseUrl = resolvedBaseUrl || defaultBaseUrl;
      return `${baseUrl}/api/v2`;
    })(),
    
    timeout: parseInt(getEnv('VITE_API_TIMEOUT', 'REACT_APP_API_TIMEOUT', '60000'), 10),
    tokenKey: getEnv('VITE_JWT_TOKEN_KEY', 'REACT_APP_JWT_TOKEN_KEY', 'auth_token'),
    userDataKey: getEnv('VITE_USER_DATA_KEY', 'REACT_APP_USER_DATA_KEY', 'user'),
    useAuthCookies: false,
  },

  // Feature flags
  features: {
    errorTracking: getEnv('VITE_ENABLE_ERROR_TRACKING', 'REACT_APP_ENABLE_ERROR_TRACKING', 'false') === 'true',
    analytics: getEnv('VITE_ENABLE_ANALYTICS', 'REACT_APP_ENABLE_ANALYTICS', 'false') === 'true',
  },

  // Application information
  app: {
    name: getEnv('VITE_APP_NAME', 'REACT_APP_NAME', 'NSIA ASSET MANAGEMENT'),
    version: getEnv('VITE_APP_VERSION', 'REACT_APP_VERSION', '1.0.0'),
    environment: runtimeEnv,
    isDevelopment: runtimeEnv === 'development',
    isProduction: isProductionEnv,
  },

  // Enterprise mode (NSIA ASSET MANAGEMENT)
  enterprise: {
    // La donnée "organization" continue d'exister en arrière-plan,
    // mais l'UI ne doit jamais l'exposer dans la version entreprise.
    hideOrganizationUi: getEnv('VITE_HIDE_ORGANIZATION_UI', 'REACT_APP_HIDE_ORGANIZATION_UI', 'true') === 'true',
    forceSingleOrganization: getEnv('VITE_FORCE_SINGLE_ORGANIZATION', 'REACT_APP_FORCE_SINGLE_ORGANIZATION', 'true') === 'true',
  },

  // Error handling configuration
  errors: {
    logLevel: getEnv('VITE_LOG_LEVEL', 'REACT_APP_LOG_LEVEL', 'error'), // error, warn, info, debug
    notificationDuration: parseInt(
      getEnv('VITE_ERROR_NOTIFICATION_DURATION', 'REACT_APP_ERROR_NOTIFICATION_DURATION', '5000'),
      10
    ),
  },
};

// Validation au démarrage
if (config.app.isProduction && !getEnv('VITE_API_BASE_URL', 'REACT_APP_API_BASE_URL') && !getBrowserOrigin()) {
  console.error('ERREUR CRITIQUE: API_BASE_URL n\'est pas défini en production!');
  console.error('Veuillez créer un fichier .env.production avec VITE_API_BASE_URL (ou REACT_APP_API_BASE_URL).');
}

// Log de configuration au démarrage (seulement en développement)
if (config.app.isDevelopment) {
  console.log('Configuration chargée:');
  console.log('   Environment:', config.app.environment);
  console.log('   API Base URL:', config.api.baseUrl);
  console.log('   API URL:', config.api.apiUrl);
}

export default config;

